import clsx from 'clsx'
import { useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import PriceRowLoyaltyPoints from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPoints'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import LineCircleIcon from 'LuxKit/Icons/line/LineCircleIcon'
import Group from 'components/utils/Group'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import zIndex from 'styles/tools/z-index'

interface LoyaltyEsimUpsellOptionData {
  ticketId: App.LoyaltyEsimUpsellTicketId
  title: string
  description: string
  badge: string | null
  isCurrent: boolean
  addonPrice: number
  earnPoints?: number
  pointsEarnRatio?: number
}

interface Props {
  option: LoyaltyEsimUpsellOptionData
  checked: boolean
  displayTier: App.LoyaltyTier
  currentLabel: string
  totalSuffix: string
  plusLabel: string
  onSelect: (ticketId: App.LoyaltyEsimUpsellTicketId) => void
}

const OptionCard = styled(Clickable)`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  width: 100%;
  padding: ${rem(24)} ${rem(16)} ${rem(16)};
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s, transform 0.12s;
  border-radius: ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  &.is-selected {
    border-color: ${props => props.theme.palette.neutral.surface.normalContrast};
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }
`

const OptionTextContent = styled(Group)`
  flex: 1 0 0;
  min-width: 0;
`

const FloatingBadge = styled(PromotionLabel)`
  position: absolute;
  top: 0;
  inset-inline-start: ${rem(16)};
  transform: translateY(-50%);
  z-index: ${zIndex.min};
  max-width: calc(100% - ${rem(32)});
`

const SelectionIcon = styled(Group)`
  flex-shrink: 0;
  width: ${rem(24)};
  height: ${rem(24)};
`

function LoyaltyEsimUpsellOption({
  option,
  checked,
  displayTier,
  currentLabel,
  totalSuffix,
  plusLabel,
  onSelect,
}: Props) {
  const onClick = useCallback(() => {
    onSelect(option.ticketId)
  }, [onSelect, option.ticketId])

  const earnPoints = option.earnPoints ?? 0
  const pointsEarnRatio = option.pointsEarnRatio ?? 0

  return (
    <OptionCard
      className={clsx({ 'is-selected': checked })}
      onClick={onClick}
    >
      {option.badge && (
        <FloatingBadge variant="branded">
          {option.badge}
        </FloatingBadge>
      )}
      <OptionTextContent direction="vertical" gap={2}>
        <Heading variant="heading6">{option.title}</Heading>
        <BodyText variant="medium" colour="neutral-two">
          {option.description}
        </BodyText>
      </OptionTextContent>
      <Group direction="horizontal" verticalAlign="center" gap={28}>
        <Group direction="vertical" horizontalAlign="end" gap={4}>
          {option.isCurrent && (
            <BodyText variant="large" weight="bold" colour="neutral-two" as="span">
              {currentLabel}
            </BodyText>
          )}
          {!option.isCurrent && <>
            <Group direction="horizontal" verticalAlign="baseline" horizontalAlign="end" gap={4} wrap="wrap">
              <BodyText variant="large" weight="bold" as="span">
                {plusLabel}
              </BodyText>
              <BodyText variant="large" weight="bold" as="span">
                <FormatCurrency value={option.addonPrice} />
              </BodyText>
              <Caption variant="medium" colour="neutral-three" weight="normal" as="span">
                {totalSuffix}
              </Caption>
            </Group>
            {earnPoints > 0 && (
              <PriceRowLoyaltyPoints
                tier={displayTier}
                size="S"
                calculationType="exact"
                earnablePoints={earnPoints}
                pointsEarnRatio={pointsEarnRatio}
              />
            )}
          </>}
        </Group>
        <SelectionIcon direction="horizontal" verticalAlign="center">
          {checked ? <SolidCheckCircleIcon /> : <LineCircleIcon colour="neutral-three" />}
        </SelectionIcon>
      </Group>
    </OptionCard>
  )
}

export default LoyaltyEsimUpsellOption
